import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'recent_products';
const MAX_RECENT_PRODUCTS = 25;

export interface RecentProduct {
  product_id: string;
  product_name: string;
  product_sku?: string;
  product_image_url?: string;
  last_price: number;
  last_quantity: number;
  added_at: string;
}

export function useRecentProducts() {
  const [recentProducts, setRecentProducts] = useState<RecentProduct[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading recent products from localStorage:', error);
    }
    return [];
  });

  // Salvar no localStorage quando mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(recentProducts));
    } catch (error) {
      console.error('Error saving recent products to localStorage:', error);
    }
  }, [recentProducts]);

  const addToRecent = useCallback((product: RecentProduct) => {
    setRecentProducts(prev => {
      // Remove se já existir
      const filtered = prev.filter(p => p.product_id !== product.product_id);
      
      // Adiciona no início com timestamp atualizado
      const updated = [
        { ...product, added_at: new Date().toISOString() },
        ...filtered
      ];
      
      // Limita ao máximo
      return updated.slice(0, MAX_RECENT_PRODUCTS);
    });
  }, []);

  const removeFromRecent = useCallback((productId: string) => {
    setRecentProducts(prev => prev.filter(p => p.product_id !== productId));
  }, []);

  const clearRecent = useCallback(() => {
    setRecentProducts([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const getRecentProductIds = useCallback(() => {
    return recentProducts.map(p => p.product_id);
  }, [recentProducts]);

  return {
    recentProducts,
    addToRecent,
    removeFromRecent,
    clearRecent,
    getRecentProductIds,
  };
}
